/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App, Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import React, { useEffect, useMemo, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  applyGameApiError,
  isGameStarted,
  useCreateGame,
  useDeleteGame,
  useUpdateGame,
  validateGameForm,
} from '@sportninja/common/features/game/gameForm'
import type {
  GameEntityLike,
  GameFormMode,
} from '@sportninja/common/features/game/gameForm'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import WarningModal from 'src/components/WarningModal'
import PlayerIdChip from 'src/features/profile/player/setupModal/components/PlayerIdChip'
import GameForm from './GameForm'
import type { SearchFieldOption } from './GameSearchField'
import { useGameForm } from '../hooks/useGameForm'
import useAllowPlaceholderVisitor from '@sportninja/common/features/game/placeholderVisitor/useAllowPlaceholderVisitor'

// Mirrors the Invite Family Member modal so both wear the same chrome.
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  /*
   * Sits BELOW the LocationModal (10000/10001) this can open on top of, so that
   * modal's mask covers this one instead of both rendering at the same depth
   * and bleeding through each other.
   *
   * Lowering this rather than raising LocationModal: that component has four
   * other consumers, and raising it would shift them all.
   */
  wrapper: { zIndex: 9999 },
  mask: { backgroundColor: 'rgba(6, 10, 16, 0.8)', zIndex: 9998 },
  footer: { paddingLeft: '16px', paddingRight: '16px' },
}

export interface GameModalProps {
  isOpen: boolean
  onClose: () => void
  mode: GameFormMode
  /** Edit only. The parsed game entity seeding the form. */
  game?: GameEntityLike | null
  /** Create only. The competition the game is added to. */
  scheduleId?: string | null
  /** Fired after a successful create/update, with the API response envelope. */
  onSaved?: (response: unknown) => void
  /** Fired after a successful delete. */
  onDeleted?: () => void
  /**
   * Whether deleting is offered, when the caller knows better than the game
   * entity does.
   *
   * The Game page passes the PARSED entity, which carries `permission.delete`
   * and `rootSchedule.is_tournament`, so the gate below resolves on its own.
   * The games GRID passes the raw list resource, which carries neither — there
   * the competition page supplies the answer, since it holds the schedule's
   * permissions and tournament flag.
   */
  canDelete?: boolean

  teamOptions: SearchFieldOption[]
  /**
   * The raw team records, needed only for their `is_placeholder` flag — the
   * option pairs above carry label and value, not that. A placeholder on
   * either side is what surfaces the division select.
   */
  teams?: Array<{ id: string; is_placeholder?: boolean | null }>
  /**
   * The raw venue records, needed for their `timezone` — selecting a venue
   * writes it into the form so the create path can re-zone the start time.
   */
  venues?: Array<{ id: string; timezone?: string | null }>
  venueOptions: SearchFieldOption[]
  facilityOptions: SearchFieldOption[]
  gameTypeOptions: SearchFieldOption[]
  subCompetitionOptions?: SearchFieldOption[]
  /** What the competition calls its child level — "Division", "Pool", … */
  subCompetitionLabel?: string
  subCompetitionsLoading?: boolean
  /**
   * Whether the division list has actually answered. Distinct from
   * `subCompetitionsLoading`: the query is IDLE until a placeholder team is
   * selected, and an idle query is neither loading nor answered.
   */
  subCompetitionsResolved?: boolean
  /**
   * Reports whether a placeholder team is selected, so the container can fetch
   * the division list only when it is actually needed.
   */
  onPlaceholderTeamChange?: (needed: boolean) => void
  teamsLoading?: boolean
  venuesLoading?: boolean
  facilitiesLoading?: boolean
  locationScopeToggle?: React.ReactNode
  createLocationRow?: React.ReactNode
  /** Lets the container fetch facilities for whichever location is selected. */
  onVenueChange?: (venueId: string | null) => void
  /**
   * Selects a venue from outside the form — used to pick up a location the
   * user just created in the nested modal.
   */
  preselectVenueId?: string | null
}

/**
 * Add/Edit Game.
 *
 * Screen-agnostic: everything it needs arrives as props, so it can be mounted
 * from the Schedule page, the Game page, or anywhere else. It owns the save,
 * the delete confirmation and the error surface; the option lists are supplied
 * by the caller.
 */
const GameModal: React.FC<GameModalProps> = ({
  isOpen,
  onClose,
  mode,
  game,
  scheduleId,
  onSaved,
  onDeleted,
  canDelete: canDeleteOverride,
  teamOptions,
  teams,
  venues,
  venueOptions,
  facilityOptions,
  gameTypeOptions,
  subCompetitionOptions,
  subCompetitionLabel,
  subCompetitionsLoading,
  subCompetitionsResolved,
  onPlaceholderTeamChange,
  teamsLoading,
  venuesLoading,
  facilitiesLoading,
  locationScopeToggle,
  createLocationRow,
  onVenueChange,
  preselectVenueId,
}) => {
  const { message } = App.useApp()
  const isEdit = mode === 'edit'

  const {
    values,
    change,
    hasError,
    hasPlaceholderTeam,
    slots,
    fieldErrors,
    setFieldErrors,
  } = useGameForm({
    mode,
    game,
    isOpen,
    teams,
    venues,
    subCompetitionOptions,
    subCompetitionsResolved,
  })

  const [confirmingDelete, setConfirmingDelete] = useState(false)

  // Let the container know whether the division list is needed at all.
  useEffect(() => {
    onPlaceholderTeamChange?.(hasPlaceholderTeam)
  }, [hasPlaceholderTeam, onPlaceholderTeamChange])

  // Keep the container's facility query pointed at the selected location.
  useEffect(() => {
    onVenueChange?.(values.venueId)
  }, [values.venueId, onVenueChange])

  // Adopt a location created in the nested modal.
  useEffect(() => {
    if (preselectVenueId && preselectVenueId !== values.venueId) {
      change({ venueId: preselectVenueId })
    }
    // Only react to a NEW id arriving; re-running on every venueId edit would
    // fight the user if they then picked a different location.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectVenueId])

  const createMutation = useCreateGame()
  const updateMutation = useUpdateGame()
  const deleteMutation = useDeleteGame()

  const isSaving = createMutation.isPending || updateMutation.isPending

  // Matches the legacy gate: delete is offered only with the permission and
  // never on a tournament, where the bracket owns the fixture. An explicit
  // override wins, for callers whose game object cannot answer either question.
  const canDelete =
    isEdit &&
    (canDeleteOverride ??
      (Boolean(game?.permission?.delete) && !game?.rootSchedule?.is_tournament))

  //  Reads the timestamps as well as the parsed booleans, so a started game
  //  locks its teams whether it was opened from the Game page or the grid.
  const teamsDisabled = isGameStarted(game)

  const applyApiError = (err: unknown) =>
    applyGameApiError(err, {
      setFieldErrors,
      notifyError: message.error,
    })

  /*
   * Client-side gate, deliberately narrow: only Duration. Everything else is
   * still validated by the API, whose `invalid_fields` response fills the same
   * `fieldErrors` map -- so the two never render differently.
   */
  const handleSubmit = async () => {
    const clientErrors = validateGameForm(values)
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors)
      return
    }

    try {
      const response = isEdit
        ? await updateMutation.mutateAsync({
            gameId: game!.id!,
            values,
            homeTeamSlotId: slots.homeTeamSlotId,
            visitingTeamSlotId: slots.visitingTeamSlotId,
          })
        : await createMutation.mutateAsync({
            scheduleId: scheduleId!,
            values,
          })

      message.success(
        isEdit ? 'Game updated successfully' : 'Game created successfully'
      )
      onSaved?.(response)
      onClose()
    } catch (err) {
      applyApiError(err)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ gameId: game!.id! })
      setConfirmingDelete(false)
      // No success toast: deleting navigates back to the competition, and the
      // game's absence from the list is the confirmation.
      onDeleted?.()
      onClose()
    } catch (err) {
      setConfirmingDelete(false)
      applyApiError(err)
    }
  }

  const header = useMemo(
    () => (
      <span css={headingStyle}>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(
            isEdit ? t('Web:editGame', {}) : t('Web:createANewGame', {})
          )}
        </Text>
        {isEdit && game?.id && (
          <PlayerIdChip
            playerId={game.id}
            label='SN GAME ID'
            idName='SN Game ID'
          />
        )}
      </span>
    ),
    [isEdit, game?.id]
  )

  const footer = (
    <div css={footerStyle}>
      {canDelete && (
        <Button
          text={`${t('common:delete', {})} ${t('common:game', {})}`}
          onClick={() => setConfirmingDelete(true)}
          variant='danger'
          disabled={isSaving}
          style={deleteButtonStyle}
        />
      )}
      <div css={footerActionsStyle}>
        <Button
          text={t('common:cancel', {})}
          onClick={onClose}
          variant='secondary'
          disabled={isSaving}
        />
        <Button
          text={t('common:confirm', {})}
          onClick={handleSubmit}
          variant='primary'
          isLoading={isSaving}
          disabled={isSaving}
        />
      </div>
    </div>
  )

  /*
   * The competition's own gate. On EDIT the game's own data wins: a game that
   * is ALREADY a placeholder stays editable even if the setting was later
   * turned off, so an existing game never becomes unfixable (SN-8410).
   */
  const { allowPlaceholderVisitor } = useAllowPlaceholderVisitor(
    values.scheduleId ?? scheduleId ?? null
  )
  const canUsePlaceholderVisitor =
    allowPlaceholderVisitor || (mode === 'edit' && values.usePlaceholderVisitor)

  /*
   * The SAME signal that locks the team pickers, rather than a second copy of
   * the rule: `isGameStarted` already covers `completed` and `ended_at`, which
   * matters because a placeholder game is FINALIZED without ever starting --
   * the final-score endpoint fills `ended_at` and only may backfill
   * `started_at` (SN-8410).
   */
  const isPlaceholderVisitorLocked = teamsDisabled

  if (!isOpen) {
    return null
  }

  return (
    <>
      <Modal
        css={modalCss}
        width={720}
        closeIcon={<CloseOutlined css={closeIconStyle} />}
        title={header}
        footer={footer}
        open={isOpen}
        onCancel={onClose}
        centered
        styles={modalStyles}
        maskClosable={false}
      >
        <GameForm
          mode={mode}
          values={values}
          onChange={change}
          hasError={hasError}
          fieldErrors={fieldErrors}
          teamOptions={teamOptions}
          venueOptions={venueOptions}
          facilityOptions={facilityOptions}
          gameTypeOptions={gameTypeOptions}
          subCompetitionOptions={subCompetitionOptions}
          subCompetitionLabel={subCompetitionLabel}
          subCompetitionsLoading={subCompetitionsLoading}
          teamsLoading={teamsLoading}
          venuesLoading={venuesLoading}
          facilitiesLoading={facilitiesLoading}
          teamsDisabled={teamsDisabled}
          canUsePlaceholderVisitor={canUsePlaceholderVisitor}
          isPlaceholderVisitorLocked={isPlaceholderVisitorLocked}
          hasPlaceholderTeam={hasPlaceholderTeam}
          locationScopeToggle={locationScopeToggle}
          createLocationRow={createLocationRow}
        />
      </Modal>

      <WarningModal
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={handleDelete}
        title={t('Web:areYouSure', {})}
        description={t('Web:deletionCantBeUndone', {})}
        cancelLabel={t('common:cancel', {}).toUpperCase()}
        confirmLabel={t('common:delete', {}).toUpperCase()}
        loadingLabel='DELETING…'
        isLoading={deleteMutation.isPending}
        destructive
      />
    </>
  )
}

export default GameModal

// ─── Styles ────────────────────────────────────────────────────────────────

const modalCss = css`
  display: flex;
  flex: 1;
  max-height: 90vh;
  margin: 24px auto;
  padding: 0;

  .ant-modal-content {
    max-height: 90vh;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }
  .ant-modal-body {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
`

/*
 * Title left, id pill hard against the RIGHT edge — the same placement
 * `PlayerIdChip` gets everywhere else it is used. `ScrollSpyLayout/Section`
 * pins it with `margin-left: auto` and `PersonalSection` stacks its chips
 * `flex-end`; this modal reaches for the chip directly rather than through a
 * Section, and stacking it under the title on the left was the one place in
 * the app that broke the convention.
 *
 * Wraps rather than truncates the pill: the id is there to be read and copied,
 * so on a narrow modal it drops to its own line instead of being clipped. The
 * TITLE is what gives way first — `min-width: 0` on the child below lets it
 * ellipsize while the pill keeps its full width.
 */
const headingStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  /* Keep the SN Game ID chip clear of the modal's close button. */
  padding-right: 32px;

  > *:first-of-type {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const closeIconStyle = css`
  color: ${colors.SOLITUDE};
  font-size: 16px;
`

const footerStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const footerActionsStyle = css`
  display: flex;
  gap: 16px;
  margin-left: auto;
  width: 50%;
`

// Sizing only -- the colour comes from the shared `danger` variant so this
// button matches every other destructive action in the app.
const deleteButtonStyle = css`
  flex: 0 0 auto;
  padding: 0 20px;
`
